import { IonButton, IonContent, IonIcon, IonPage, IonProgressBar } from '@ionic/react';
import classNames from 'classnames';
import { print } from 'ionicons/icons';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DqText } from 'src/components/dq-text';
import JobPressureCard from 'src/components/job-details/JobPressureCard';
import TubeSheetCard from 'src/components/job-details/TubeSheetCard';
import { JobSummaryCard } from 'src/components/job-details/job-summary/JobSummaryCard';
import { PrintContainer } from 'src/components/print-container/PrintContainer';
import { ChipType } from 'src/components/reusable-components/Chip';
import DesktopDetailPageHeader from 'src/components/reusable-components/desktop-header/DesktopDetailPageHeader';
import IdleVsHp from 'src/components/visualizations/IdleVsHp';
import PieHighPressure from 'src/components/visualizations/pieHighPressure';
import { PieSentinelSystem } from 'src/components/visualizations/pieSentinelSystem';
import { JobMessages } from 'src/intl/JobMessages';
import { JobLogV2Data } from 'src/utils/logs/types';
import { TubesCleanedGraph } from '../../components/job-details/TubesCleanedGraph';
import { TubeSheetChartType } from '../../components/job-details/util/types';
import { JobLog, LogPrintConfig } from '../../models/jobLog';
import { RangeSet } from '../../components/visualizations/utils';
import { getControllerOffRanges } from './util';

interface LogInfoPrintProps {
  /** The job log info from the sentinel system. This may not exist in all circumstances (i.e. for job logs created offline). */
  info: JobLog | undefined;
  /** The parsed job log data. May be undefined while it is being loaded. */
  data: JobLogV2Data | undefined;
  /** The status of the job. */
  status: 'complete' | 'in-progress' | 'live';
  /** The formatted display name of the job. */
  description: string | undefined;
  subheading: string | undefined;
  orgName: string | undefined;
  /** Describes the progress of fetching all of the job log information, expressed as a number from 0 to 1. If undefined, does not apply. */
  fetchProgress?: number;
  /** Indicates if there were errors fetching/parsing the log data */
  hasErrors: boolean;
  /** Configuration used to determine what tables and controls are displayed */
  printConfig: LogPrintConfig;
  /** The time zone that the job log was created in. */
  timezone: string;
  isLimitedMode: boolean;
}

export const LogInfoPrint: FC<LogInfoPrintProps> = ({
  info,
  data,
  status,
  description,
  subheading,
  fetchProgress,
  hasErrors,
  isLimitedMode,
  printConfig,
  timezone,
  orgName,
}) => {
  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 1000);
  };

  const { formatMessage } = useIntl();

  const timeRange = useMemo(
    () => ({ min: printConfig.startTime ?? 0, max: printConfig.endTime ?? data?.msElapsed ?? 1 }),
    [data?.msElapsed, printConfig.endTime, printConfig.startTime],
  );

  const controllerOffRanges = useMemo<RangeSet>(() => {
    const powerEvents = data?.powerEvents || [];
    const msElapsed = data?.msElapsed || 0;
    return getControllerOffRanges(powerEvents, msElapsed);
  }, [data]);
  const activeRunTime = controllerOffRanges.sumExcluded();

  const logFileTimeRangeMin = useMemo(() => {
    let newMin = timeRange.min;
    const ranges = controllerOffRanges.getRanges();
    ranges.forEach(range => {
      if (range.start < newMin) {
        const rangeMs = range.end - range.start;
        newMin += rangeMs;
      } else {
        return;
      }
    });

    return newMin;
  }, [timeRange.min, controllerOffRanges]);

  const logFileTimeRangeMax = useMemo(() => {
    let newMax = timeRange.max;
    const ranges = controllerOffRanges.getRanges();
    ranges.forEach(range => {
      if (range.start < newMax) {
        const rangeMs = range.end - range.start;
        newMax += rangeMs;
      } else {
        return;
      }
    });

    return newMax;
  }, [timeRange.max, controllerOffRanges]);

  return (
    <IonPage>
      <DesktopDetailPageHeader
        heading={description || formatMessage(JobMessages.jobDetails)}
        subheading={subheading}
        chipProps={{
          title:
            status === 'live'
              ? formatMessage(JobMessages.jobLive)
              : status === 'in-progress'
                ? formatMessage(JobMessages.jobInProgress)
                : formatMessage(JobMessages.jobComplete),
          type:
            status === 'live'
              ? ChipType.JOB_LIVE
              : status === 'in-progress'
                ? ChipType.JOB_INPROGRESS
                : ChipType.JOB_COMPLETE,
        }}
        left={true}
        right={
          <IonButton onClick={handlePrint}>
            <IonIcon slot="icon-only" color="dark" size="large" icon={print} />
          </IonButton>
        }
      />

      <IonContent>
        <IonProgressBar
          value={fetchProgress}
          slot="fixed"
          className={classNames(
            'job-info-progress',
            (fetchProgress === 1 || !fetchProgress) && 'job-info-progress-hidden',
          )}
        />
        <div className="ion-padding">
          {data && (
            <PrintContainer>
              <JobSummaryCard
                info={info}
                data={data}
                showTimeDetails={printConfig.showTimestamps}
                showEstimatedTimeToCompletion={printConfig.showEstimatedTimeToCompletion}
                showUpdateJobButton={false}
                jobStatus={status}
                description={info?.description}
                siteName={info?.siteName}
                heatExchangerModelNumber={info?.heatExchangerModelNumber}
                timeRangeMin={logFileTimeRangeMin}
                timeRangeMax={logFileTimeRangeMax}
                timezone={timezone}
                activeRunTime={activeRunTime}
              />

              {/** No time range card on print out */}

              {printConfig.showTubesCleaned && (
                <TubesCleanedGraph
                  tubeCount={data.tubeCount}
                  timeTable={data.timeTable}
                  timeRangeMin={timeRange.min}
                  timeRangeMax={timeRange.max}
                  timezone={timezone}
                  showAnnotations={printConfig.showAnnotations}
                  showTimestamps={printConfig.showTimestamps}
                  showAnnotationUser={printConfig.showAnnotationUser}
                  showAnnotationActions={false}
                  orgName={orgName}
                  controllerOffRanges={controllerOffRanges}
                />
              )}

              {printConfig.showTubeSheetCompletion && (
                <TubeSheetCard
                  data={data}
                  timeRangeMin={logFileTimeRangeMin}
                  timeRangeMax={logFileTimeRangeMax}
                  timezone={timezone}
                  showTimeDetails={printConfig.showTimestamps}
                />
              )}

              {printConfig.showTubeSheetTime && (
                <TubeSheetCard
                  data={data}
                  timeRangeMin={logFileTimeRangeMin}
                  timeRangeMax={logFileTimeRangeMax}
                  timezone={timezone}
                  showTimeDetails={printConfig.showTimestamps}
                  preselectedTubeSheetType={TubeSheetChartType.HEAT}
                />
              )}

              {printConfig.showTubeSheetDepthCleaned && (
                <TubeSheetCard
                  data={data}
                  timeRangeMin={logFileTimeRangeMin}
                  timeRangeMax={logFileTimeRangeMax}
                  timezone={timezone}
                  showTimeDetails={printConfig.showTimestamps}
                  preselectedTubeSheetType={TubeSheetChartType.DEPTH}
                />
              )}

              {printConfig.showTubeSheetAutostrokes && (
                <TubeSheetCard
                  data={data}
                  timeRangeMin={logFileTimeRangeMin}
                  timeRangeMax={logFileTimeRangeMax}
                  timezone={timezone}
                  showTimeDetails={printConfig.showTimestamps}
                  preselectedTubeSheetType={TubeSheetChartType.AUTOSTROKES}
                />
              )}

              {printConfig.showTubeSheetLanceSpeed && (
                <TubeSheetCard
                  data={data}
                  timeRangeMin={logFileTimeRangeMin}
                  timeRangeMax={logFileTimeRangeMax}
                  timezone={timezone}
                  showTimeDetails={printConfig.showTimestamps}
                  preselectedTubeSheetType={TubeSheetChartType.LANCESPEED}
                />
              )}

              {printConfig.showPieCharts && (
                <PieSentinelSystem
                  title={formatMessage(JobMessages.systemHeading)}
                  valve5={data.valveStatus[4]}
                  valve6={data.valveStatus[5]}
                  valve2={data.valveStatus[1]}
                  timeTable={data.timeTable}
                  timeRange={timeRange}
                  showTimeDetails={printConfig.showTimestamps}
                  controllerOffRanges={controllerOffRanges}
                  logFileTimeRangeMin={logFileTimeRangeMin}
                  logFileTimeRangeMax={logFileTimeRangeMax}
                />
              )}
              {printConfig.showPieCharts && (
                <PieHighPressure
                  title={formatMessage(JobMessages.highPressureHeading)}
                  valve3={data.valveStatus[2]}
                  valve16={data.valveStatus[15]}
                  timeTable={data.timeTable}
                  timeRange={timeRange}
                  showTimeDetails={printConfig.showTimestamps}
                  controllerOffRanges={controllerOffRanges}
                  logFileTimeRangeMin={logFileTimeRangeMin}
                  logFileTimeRangeMax={logFileTimeRangeMax}
                />
              )}

              {printConfig.showIdleVsPressure && (
                <IdleVsHp
                  valve2={data.valveStatus[1]}
                  valve3={data.valveStatus[2]}
                  valve5={data.valveStatus[4]}
                  valve6={data.valveStatus[5]}
                  valve16={data.valveStatus[15]}
                  showTimeDetails={printConfig.showTimestamps}
                  controllerOffRanges={controllerOffRanges}
                  logFileTimeRangeMin={logFileTimeRangeMin}
                  logFileTimeRangeMax={logFileTimeRangeMax}
                />
              )}
              {printConfig.showPressures && (
                <JobPressureCard
                  clampData={data.clamp}
                  inletData={data.inlet}
                  waterData={data.water}
                  timeTable={data.timeTable}
                  timeRangeMin={logFileTimeRangeMin}
                  timeRangeMax={logFileTimeRangeMax}
                  timezone={timezone}
                  showTimestamps={printConfig.showTimestamps}
                />
              )}
            </PrintContainer>
          )}
          {hasErrors && fetchProgress !== 1 && (
            <div className="ion-text-center">
              <br />
              <DqText variant="h2" color="dark">
                {formatMessage(JobMessages.errorFetchingLogDataTitle)}
              </DqText>
              <br />
              {!!isLimitedMode && (
                <div>
                  <DqText variant="body-strong" color="medium">
                    {formatMessage(JobMessages.youMustGoOnlineDescription)}
                  </DqText>
                </div>
              )}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};
